import { type FC, type ReactNode, useMemo } from 'react';
import { useCurrentUser } from '../../hooks';
import { QBox } from '../../QLayouts';
import type {
  ResolvedFilter,
  FilterDefinition,
} from '../../QOrganisms/Table/Filtering';
import { Tag, TagCloseButton, TagLabel } from '@chakra-ui/react';

export type QActiveFilterProps = {
  filter: ResolvedFilter<unknown>;
  onRemove: () => void;
  /** Optional: customise the display of the active filter */
  render?: FilterDefinition['activeRender'];
};

export const QActiveFilter: FC<QActiveFilterProps> = ({
  filter: { label, value },
  onRemove,
  render,
}) => {
  const { formatDate } = useCurrentUser();

  const custom = useMemo(() => {
    const custom = render?.(value);
    if (!custom) {
      return undefined;
    }

    if ((custom as unknown)?.['label'] || (custom as unknown)?.['value']) {
      return custom as { label?: ReactNode; value?: ReactNode };
    }

    return {
      label: undefined,
      value: custom as ReactNode,
    };
  }, [render, value]);

  return (
    <Tag
      variant="outline"
      colorScheme="blue"
      bg="blue.50"
      pt={0.5}
      data-cy={`active-filter-${label}`}
    >
      <TagLabel>
        {custom?.label !== null && <>{custom?.label || `${label}:`}&nbsp;</>}
        <QBox
          as="span"
          fontWeight={600}
          textTransform={custom?.value ? 'none' : 'capitalize'}
        >
          {custom?.value ??
            (value instanceof Date ? formatDate(value) : `${value}`)}
        </QBox>
      </TagLabel>
      <TagCloseButton onClick={onRemove} />
    </Tag>
  );
};
